import type { PauseController, PauseChangeEvent } from '../../controller/PauseController';
import type { InputController } from '../../controller/InputController';
import type { DebugWindow } from '../../ui/DebugWindow';
import type { ItemBar } from '../../ui/ItemBar';
import type { PlayerInventory } from '../../ui/PlayerInventory';
import type { CodexWindow } from '../../ui/CodexWindow';
import type { SkillBar } from '../../ui/SkillBar';
import type { SelfCharacterWindowHandle } from '../../ui/SelfCharacterWindow';
import type { UserMenuPopupHandle } from '../../ui/UserMenuPopup';
import type { AdminWindowHandle } from '../../ui/admin';
import type { ChatBoxHandle } from '../../ui/ChatBox';
import type { MinimapHandle } from '../../ui/Minimap';
import { windowRegistry } from '../../ui/windowing/WindowRegistry';
import { getDefaultBinding, normalizeKey, type BindableAction } from '../../settings/KeyBindings';

export type InputContext = 'login' | 'gameplay' | 'paused';

interface KeyBindingsPanelLike {
  show(): void;
  hide(): void;
}

interface SetupHotkeysParams {
  pauseController: PauseController;
  inputController: InputController;
  playerInventory: PlayerInventory;
  selfCharacterWindow: SelfCharacterWindowHandle;
  chatBox: ChatBoxHandle;
  minimap: MinimapHandle;
  codexWindow: CodexWindow;
  debugWindow: DebugWindow;
  adminWindow: AdminWindowHandle;
  inventoryOverlay: HTMLDivElement;
  codexOverlay: HTMLDivElement;
  debugOverlay: HTMLDivElement;
  itemBarUtils: ItemBar;
  skillBarUtils: SkillBar | null;
  keyBindingsPanel: KeyBindingsPanelLike;
  isKeyBindingsPanelVisible: () => boolean;
  getUserMenuPopupHandle: () => UserMenuPopupHandle | null;
}

export interface HotkeyController {
  setContext(next: InputContext): void;
  getContext(): InputContext;
  dispose(): void;
}

export function setupHotkeys(params: SetupHotkeysParams): HotkeyController {
  const {
    pauseController,
    inputController,
    playerInventory,
    selfCharacterWindow,
    chatBox,
    minimap,
    codexWindow,
    debugWindow,
    adminWindow,
    inventoryOverlay,
    codexOverlay,
    debugOverlay,
    itemBarUtils,
    skillBarUtils,
    keyBindingsPanel,
    isKeyBindingsPanelVisible,
    getUserMenuPopupHandle,
  } = params;

  let activeContext: InputContext = 'login';
  const keyActionCallbacks = new Map<string, (event: KeyboardEvent) => boolean>();

  const setContext = (next: InputContext): void => {
    if (activeContext === next) return;
    activeContext = next;
  };

  const isTyping = (): boolean => {
    const element = document.activeElement;
    if (!(element instanceof HTMLElement)) {
      return false;
    }
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) {
      return true;
    }
    return element.isContentEditable || element.closest('input, textarea, select, [contenteditable="true"]') !== null;
  };

  const parseBarSlot = (event: KeyboardEvent): number | null => {
    const fromKey = Number.parseInt(event.key, 10);
    const fromCode = event.code.startsWith('Digit')
      ? Number.parseInt(event.code.slice('Digit'.length), 10)
      : Number.NaN;
    const keyNum = Number.isFinite(fromKey) ? fromKey : fromCode;
    if (!Number.isInteger(keyNum) || keyNum < 1 || keyNum > 9) {
      return null;
    }
    return keyNum - 1;
  };

  const handleSkillBarKeyPress = (event: KeyboardEvent): boolean => {
    if (event.shiftKey) return false;
    const slot = parseBarSlot(event);
    if (slot === null) return false;
    void skillBarUtils?.useSlot(slot);
    return true;
  };

  const handleItemBarKeyPress = (event: KeyboardEvent): boolean => {
    if (!event.shiftKey) return false;
    const slot = parseBarSlot(event);
    if (slot === null) return false;
    itemBarUtils.setSelectedIndex(slot);
    void itemBarUtils.useSelectedSlot();
    return true;
  };

  const handleUseSelectedItemKeyPress = (event: KeyboardEvent): boolean => {
    if (event.key !== 'f' && event.key !== 'F') return false;
    if (activeContext !== 'gameplay') return false;
    if (event.repeat) return true;
    void itemBarUtils.useSelectedSlot();
    return true;
  };

  const handleClearQueueKeyPress = (event: KeyboardEvent): boolean => {
    if (normalizeEventKey(event) !== normalizeKey(getDefaultBinding('clearQueue'))) return false;
    if (activeContext !== 'gameplay') return false;
    if (event.repeat) return true;
    event.preventDefault();
    inputController.requestClearQueue();
    return true;
  };

  const normalizeEventKey = (event: KeyboardEvent): string => normalizeKey(event.key);

  const registerToggleActionKey = (
    action: BindableAction,
    isOpen: () => boolean,
    open: () => void,
    close: () => void
  ): void => {
    const canonicalKey = normalizeKey(getDefaultBinding(action));
    if (keyActionCallbacks.has(canonicalKey)) {
      throw new Error(`[Hotkeys] ${canonicalKey} is already registered`);
    }
    keyActionCallbacks.set(canonicalKey, (event) => {
      if (activeContext === 'login') return false;
      if (event.repeat) return true;
      event.preventDefault();
      if (isOpen()) {
        close();
      } else {
        open();
      }
      return true;
    });
  };

  const handleRegisteredActionKeyPress = (event: KeyboardEvent): boolean => {
    const callback = keyActionCallbacks.get(normalizeEventKey(event));
    if (!callback) return false;
    return callback(event);
  };

  registerToggleActionKey(
    'toggleSocials',
    () => getUserMenuPopupHandle()?.isOpenOnTab('Socials') ?? false,
    () => getUserMenuPopupHandle()?.open('Socials'),
    () => getUserMenuPopupHandle()?.close()
  );

  registerToggleActionKey(
    'toggleCharacter',
    () => selfCharacterWindow.isOpen(),
    () => selfCharacterWindow.show(),
    () => selfCharacterWindow.hide()
  );

  registerToggleActionKey(
    'toggleInventory',
    () => playerInventory.isOpenOnTab('items'),
    () => playerInventory.openTab('items'),
    () => playerInventory.hide()
  );

  registerToggleActionKey(
    'toggleSkills',
    () => playerInventory.isOpenOnTab('skills'),
    () => playerInventory.openTab('skills'),
    () => playerInventory.hide()
  );

  registerToggleActionKey(
    'toggleChat',
    () => chatBox.isVisible(),
    () => chatBox.show(),
    () => chatBox.hide()
  );

  registerToggleActionKey(
    'toggleMinimap',
    () => minimap.isVisible(),
    () => minimap.show(),
    () => minimap.hide()
  );

  registerToggleActionKey(
    'toggleCodex',
    () => codexWindow.isOpenOnTab('skills'),
    () => codexWindow.openTab('skills'),
    () => codexWindow.hide()
  );

  registerToggleActionKey(
    'toggleEquipment',
    () => playerInventory.isOpenOnTab('equipment'),
    () => playerInventory.openTab('equipment'),
    () => playerInventory.hide()
  );

  registerToggleActionKey(
    'toggleAdmin',
    () => adminWindow.isOpenOnTab('map-editor'),
    () => adminWindow.openTab('map-editor'),
    () => adminWindow.hide()
  );

  registerToggleActionKey(
    'toggleDebug',
    () => debugWindow.isOpenOnTab('events'),
    () => debugWindow.openTab('events'),
    () => debugWindow.hide()
  );

  registerToggleActionKey(
    'toggleKeyBindings',
    () => isKeyBindingsPanelVisible(),
    () => keyBindingsPanel.show(),
    () => keyBindingsPanel.hide()
  );

  const handleKeyDown = (event: KeyboardEvent): void => {
    if (isTyping()) return;

    if (event.key === 'Escape' && activeContext !== 'login' && windowRegistry.consumeEscape()) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    if (activeContext !== 'login' && pauseController.handleInput(event)) {
      return;
    }

    // G is the interact canonical action; prioritize gameplay intent enqueueing
    // before toggle/action hotkeys so it cannot be consumed by other bindings.
    if (activeContext === 'gameplay' && (event.key === 'g' || event.key === 'G')) {
      if (inputController.handleInput(event)) {
        return;
      }
    }

    if (handleRegisteredActionKeyPress(event)) {
      return;
    }

    if (activeContext === 'gameplay' && handleSkillBarKeyPress(event)) {
      return;
    }

    if (activeContext === 'gameplay' && handleItemBarKeyPress(event)) {
      return;
    }

    if (handleUseSelectedItemKeyPress(event)) {
      return;
    }

    if (handleClearQueueKeyPress(event)) {
      return;
    }

    if (activeContext === 'gameplay') {
      inputController.handleInput(event);
    }
  };

  const handlePauseChanged = (event: Event): void => {
    if (activeContext === 'login') return;
    const { paused } = (event as PauseChangeEvent).detail;
    setContext(paused ? 'paused' : 'gameplay');
    if (!paused) {
      keyBindingsPanel.hide();
      playerInventory.hide();
      codexWindow.hide();
      adminWindow.hide();
      debugWindow.hide();
    }
  };

  document.addEventListener('keydown', handleKeyDown);
  pauseController.addEventListener('pauseChanged', handlePauseChanged as EventListener);

  return {
    setContext,
    getContext: () => activeContext,
    dispose: () => {
      document.removeEventListener('keydown', handleKeyDown);
      pauseController.removeEventListener('pauseChanged', handlePauseChanged as EventListener);
    },
  };
}
